import React, { useState } from 'react';
import { Calendar, Tag, Star, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { blogPageData, policiesPageData, reviewPageData } from '../data/mockData';

// ==================== BLOG PAGE ====================
export const BlogPage = () => {
  return (
    <PageLayout scriptLabel={blogPageData.scriptLabel} heading={blogPageData.heading}>
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-8">
            {blogPageData.posts.map((post, i) => (
              <article
                key={i}
                className="bg-white border border-gray-100 rounded-sm p-8 shadow-sm hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center gap-4 mb-4">
                  <span className="inline-flex items-center gap-1 bg-[#F3ECF8] text-[#5B2C6F] font-open-sans text-xs tracking-[0.1em] uppercase px-3 py-1 rounded-sm">
                    <Tag size={12} />
                    {post.category}
                  </span>
                  <span className="flex items-center gap-1 text-[#999] font-open-sans text-xs">
                    <Calendar size={12} />
                    {post.date}
                  </span>
                </div>
                <h2 className="font-playfair text-[#1a1a1a] text-xl md:text-2xl font-bold mb-4">
                  {post.title}
                </h2>
                <p className="font-open-sans text-[#888] text-[15px] leading-relaxed mb-4">
                  {post.excerpt}
                </p>
                <Link
                  to={`/blog/${post.slug}`}
                  className="inline-flex items-center gap-3 text-[#C9A84C] font-open-sans text-xs tracking-[0.2em] uppercase hover:text-[#B89740] transition-colors duration-300"
                >
                  READ MORE
                  <span className="text-lg">—</span>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

// ==================== POLICIES PAGE ====================
export const PoliciesPage = () => {
  return (
    <PageLayout scriptLabel={policiesPageData.scriptLabel} heading={policiesPageData.heading}>
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-10">
            {policiesPageData.policies.map((policy, i) => (
              <div key={i}>
                <h2 className="font-open-sans text-[#1a1a1a] text-sm tracking-[0.2em] font-bold uppercase mb-4">
                  {policy.title}
                </h2>
                <div className="w-16 h-[2px] bg-[#C9A84C] mb-4" />
                <p className="font-open-sans text-[#666] text-[15px] leading-relaxed">
                  {policy.content}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

// ==================== REVIEW PAGE ====================

// --- validation helpers (mirror backend rules, defense-in-depth) ---
const NAME_REGEX = /^[\p{L}\s\-.'’]+$/u;
const LIMITS = {
  name: { min: 2, max: 80 },
  review: { min: 5, max: 2000 },
};

const sanitizeInput = (value) => {
  if (value == null) return '';
  return String(value)
    .replace(/<[^>]*>/g, '') // strip tags
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, ''); // strip control chars
};

const validateReviewField = (name, rawValue) => {
  const value = sanitizeInput(rawValue).trim();
  switch (name) {
    case 'name': {
      if (!value) return 'Name is required.';
      if (value.length < LIMITS.name.min) return `Name must be at least ${LIMITS.name.min} characters.`;
      if (value.length > LIMITS.name.max) return `Name must be at most ${LIMITS.name.max} characters.`;
      if (!NAME_REGEX.test(value)) return 'Name contains invalid characters.';
      return '';
    }
    case 'review': {
      if (!value) return 'Review is required.';
      if (value.length < LIMITS.review.min) return `Review must be at least ${LIMITS.review.min} characters.`;
      if (value.length > LIMITS.review.max) return `Review must be at most ${LIMITS.review.max} characters.`;
      return '';
    }
    default:
      return '';
  }
};

export const ReviewPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    rating: 5,
    review: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  React.useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await fetch(`${API}/reviews`);
        if (res.ok) {
          const data = await res.json();
          setReviews(data);
        }
      } catch (err) {
        // silently fail - reviews display is optional
      }
    };
    fetchReviews();
  }, [API]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const max = name === 'review' ? LIMITS.review.max : LIMITS.name.max;
    const capped = value.slice(0, max);
    setFormData((prev) => ({ ...prev, [name]: capped }));
    setError('');
    if (touched[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: validateReviewField(name, capped) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setFieldErrors((prev) => ({ ...prev, [name]: validateReviewField(name, value) }));
  };

  const validateAll = () => {
    const errs = {};
    ['name', 'review'].forEach((key) => {
      const msg = validateReviewField(key, formData[key]);
      if (msg) errs[key] = msg;
    });
    setFieldErrors(errs);
    setTouched({ name: true, review: true });
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateAll()) {
      setError('Please fix the errors above before submitting.');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: sanitizeInput(formData.name).trim(),
        rating: Number(formData.rating),
        review: sanitizeInput(formData.review).trim(),
      };
      const res = await fetch(`${API}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let detail;
        try {
          const body = await res.json();
          detail = body?.detail;
        } catch (_) {
          /* ignore parse errors */
        }
        if (Array.isArray(detail) && detail.length > 0) {
          const messages = detail.map((d) => {
            const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : 'field';
            return `${field}: ${d.msg}`;
          });
          setError(messages.join(' • '));
        } else if (typeof detail === 'string') {
          setError(detail);
        } else {
          setError('Something went wrong. Please try again.');
        }
        return;
      }
      const newReview = await res.json();
      setSubmitted(true);
      setReviews((prev) => [newReview, ...prev]);
      setFormData({ name: '', rating: 5, review: '' });
      setFieldErrors({});
      setTouched({});
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const baseInputClass =
    'w-full rounded-sm px-4 py-3 font-open-sans text-[#333] text-base focus:outline-none transition-colors duration-300 border';
  const inputClass = (field) =>
    `${baseInputClass} ${
      fieldErrors[field]
        ? 'border-red-400 focus:border-red-500'
        : 'border-gray-200 focus:border-[#C9A84C]'
    }`;

  return (
    <PageLayout scriptLabel={reviewPageData.scriptLabel} heading={reviewPageData.heading}>
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <p className="font-open-sans text-[#666] text-lg leading-relaxed text-center mb-12">
            {reviewPageData.intro}
          </p>

          {submitted && (
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-sm p-4 mb-6" data-testid="review-success">
              <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
              <p className="font-open-sans text-green-700 text-sm">
                Thank you for your review! We truly appreciate your feedback.
              </p>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-sm p-4 mb-6" data-testid="review-error">
              <CheckCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="font-open-sans text-red-700 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            <div>
              <label htmlFor="review-name" className="font-open-sans text-[#4A154B] text-sm tracking-[0.1em] uppercase block mb-2">
                Your Name *
              </label>
              <input
                id="review-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                autoComplete="name"
                maxLength={LIMITS.name.max}
                aria-invalid={!!fieldErrors.name}
                aria-describedby={fieldErrors.name ? 'review-name-error' : undefined}
                data-testid="review-name-input"
                className={inputClass('name')}
              />
              {fieldErrors.name && (
                <p id="review-name-error" className="mt-1 text-xs text-red-600 font-open-sans" data-testid="review-name-error">
                  {fieldErrors.name}
                </p>
              )}
            </div>

            <div>
              <label className="font-open-sans text-[#4A154B] text-sm tracking-[0.1em] uppercase block mb-2">
                Rating
              </label>
              <div className="flex gap-2" data-testid="review-rating">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFormData({ ...formData, rating: star })}
                    aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                    data-testid={`review-rating-${star}`}
                    className={`transition-colors duration-200 ${
                      star <= formData.rating ? 'text-[#C9A84C]' : 'text-gray-300'
                    }`}
                  >
                    <Star size={32} fill={star <= formData.rating ? '#C9A84C' : 'none'} />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="review-text" className="font-open-sans text-[#4A154B] text-sm tracking-[0.1em] uppercase">
                  Your Review *
                </label>
                <span className="font-open-sans text-xs text-gray-400" data-testid="review-counter">
                  {formData.review.length}/{LIMITS.review.max}
                </span>
              </div>
              <textarea
                id="review-text"
                name="review"
                value={formData.review}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                rows={6}
                maxLength={LIMITS.review.max}
                placeholder="Share your experience with A Healing Touch Pain Release..."
                aria-invalid={!!fieldErrors.review}
                aria-describedby={fieldErrors.review ? 'review-text-error' : undefined}
                data-testid="review-text-input"
                className={`${inputClass('review')} resize-none`}
              />
              {fieldErrors.review && (
                <p id="review-text-error" className="mt-1 text-xs text-red-600 font-open-sans" data-testid="review-text-error">
                  {fieldErrors.review}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="review-submit-button"
              className={`inline-block px-8 py-3 bg-[#C9A84C] text-white font-open-sans text-xs tracking-[0.2em] uppercase hover:bg-[#B89740] transition-colors duration-300 ${
                loading ? 'opacity-60 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'SUBMITTING...' : 'SUBMIT REVIEW'}
            </button>
          </form>

          {/* Display submitted reviews */}
          {reviews.length > 0 && (
            <div className="mt-16">
              <div className="w-16 h-[2px] bg-[#C9A84C] mx-auto mb-8" />
              <p className="font-seaweed text-[#C9A84C] text-3xl text-center mb-2">
                What others are saying
              </p>
              <h3 className="font-playfair text-[#1a1a1a] text-2xl font-bold text-center mb-10">
                Recent Reviews
              </h3>
              <div className="space-y-6">
                {reviews.map((r, i) => (
                  <div
                    key={r.id || i}
                    className="bg-[#F3ECF8] rounded-sm p-6"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          size={18}
                          className={star <= r.rating ? 'text-[#C9A84C]' : 'text-gray-300'}
                          fill={star <= r.rating ? '#C9A84C' : 'none'}
                        />
                      ))}
                    </div>
                    <p className="font-playfair text-[#555] text-base italic leading-relaxed mb-3">
                      "{r.review}"
                    </p>
                    <p className="font-open-sans text-[#C9A84C] text-sm tracking-wider">
                      — {r.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
};
